<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Stores
	import { user_data } from 'src/stores/account/user';
	import { getDeviceStatus, getDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
	//Design system components
	import {
		Button,
		Alert,
		Text,
		toast,
		Modal,
		Container,
		Loader,
		Icon,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../../types/Device';
	import { postReq } from 'src/services/http';
	import type { AiModelData } from 'src/types/AIDeviceModelType';
	import { beforeUpdate, onMount } from 'svelte';

	import AINonPremiumUserAlert from './AINonPremiumUserAlert.svelte';
	import AIIntroductionScreen from './AIIntroductionScreen.svelte';
	import AiModelTrainingScreen from './AIModelTrainingScreen.svelte';
	import AIMonitoringScreen from './AIMonitoringScreen.svelte';
	import AIMoreModal from './AIMoreModal.svelte';
	import { MAX_AI_MODEL_LIMIT } from 'src/constants';
	import { devices_config } from '@devices/index';
	import { capFirstChar } from 'src/helpers/utilities';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { eraseOrCancelAIModel } from './ai_helper';
	import { real_id } from 'src/helpers/interface_helpers';

	$: statusStore = getDeviceStatus(id);
	export let device: Device;
	export let useNewLayout = false;
	const isPremiumUser = $user_data?.premium > 0;
	const id = device.id;
	const deviceIsOnline = $statusStore?.online;
	let skipNextUpdate = false;
	let showWarningIncorrectData = false;
	let requiredDaysOfLearning = 10;
	let requiredHours = requiredDaysOfLearning * 24;

	//check if the AI model is activated for the current device
	//if hasOwnProperty('ai_device_failure') we assumed the ai model is running or it was paused
	//if there is no ai_device_failure key in the cloud_options object, the AI model is not activated and we don't show it in the filters and showing introduction screen
	$: aiModelIsActivated = device?.cloud_options?.hasOwnProperty('ai_device_failure') || false;

	//if the AI model is activated, check if the AI model is enabled (toggle on) false = paused, true = learning or monitoring
	let enabled = device?.cloud_options?.ai_device_failure || false;
	let startTraining = false;
	let showResetCancelEraseAIModal = false;
	let showAboutAIModelModal = false;

	let progressReadinessPercent: number = 0;
	let remainingHours: number = 0;
	let starLearningTs: number = 0; // when check_ai may return 0 because the BE doesn't enough resource to check at the moment. the correct value will be returned after 1 hour
	let lastResetTs: number = 0;
	let timeToReset = 2; //2 minutes
	let resetTime = timeToReset * 60 * 1000; //2 minutes ( 4 minutes in milliseconds = 240000 = 4*60*1000)

	//show introduction screen if the AI model is activated
	$: showAIIntroductionScreen = !aiModelIsActivated;

	//by modalType what content should be shown in the modal (reset, cancel, erase)
	//modal type is used to know what action should be done when the button is clicked
	//and to show the correct text in the loading screen
	$: modalType = '';

	let loading = false;

	$: showLoadingScreen = false;
	$: showAIMonitoringScreen = progressReadinessPercent === 100 && !showLoadingScreen;

	const resetCancelEraseAIModelModalsContent = {
		reset: {
			title: $format('title_reset_ai_training', {
				default: 'Reset AI Тraining',
			}),
			text: $format('text_reset_ai_model', {
				default:
					"You are currently training the AI model. During this process the AI model gathers data on your device's usage patterns, consumption, working behavior, etc.",
			}),
			alert: $format('text_reset_ai_model_alert', {
				default:
					'Resetting the training process will erase all collected data and restart the process.',
			}),
			buttonSecondary: capFirstChar(
				$format('button_cancel', {
					default: 'Cancel',
				}),
			),
			buttonPrimary: capFirstChar(
				$format('button_reset', {
					default: 'Reset',
				}),
			),
		},
		cancel: {
			title: $format('cancel_ai_training_modal', {
				default: 'Cancel AI Training',
			}),
			text: $format('text_cancel_ai_model', {
				default:
					"You are currently training the AI model. During this process the AI model gathers data on your device's usage patterns, consumption, working behavior, etc.",
			}),
			alert: $format('text_cancel_ai_model_alert', {
				default:
					'Canceling the training process will erase all collected data and the AI model will revert to its initial state.',
			}),
			buttonSecondary: capFirstChar(
				$format('resume_training_btn', {
					default: 'Resume training',
				}),
			),
			buttonPrimary: capFirstChar(
				$format('cancel_ai_training', {
					default: 'Cancel AI training',
				}),
			),
		},
		erase: {
			title: $format('title_erase_ai_model_modal', {
				default: 'Erase AI Training Data',
			}),
			text: $format('text_erase_ai_model', {
				default:
					"Currently your AI model is fully trained on your device's usage patterns, consumption, working behavior, etc.",
			}),
			alert: $format('text_erase_ai_model_alert', {
				default:
					'Erasing AI training data will delete all collected data and the AI model will revert to its initial state.',
			}),
			buttonSecondary: capFirstChar(
				$format('button_cancel', {
					default: 'Cancel',
				}),
			),
			buttonPrimary: capFirstChar(
				$format('sub_settings_title_erase_data', {
					default: 'Erase data',
				}),
			),
		},
	};

	const resetAITraining = async () => {
		const res = await postReq(`device/reset_ai`, {
			id: id,
		});

		if (res?.isok) {
			toast.open({
				type: 'success',
				title: $format('toast_title_reset_ai_model', {
					default: 'AI model is resetting...',
				}),
			});

			//showing alert with warning for incorrect data because the AI model is reset but the new data will come after 1-2 minutes.
			showWarningIncorrectData = true;

			// TODO: show loading screen when resetting the AI model
			//show resetting screen aka loader for 1 minute and save timestamp in the storage
			//should save timestamp also in the meta data in the device. For the further work there is end point where we can save this timestamp. *Should be developed in the future by Spas and Alex
		} else {
			toast.open({
				type: 'danger',
				title: $format('toast_title_reset_ai_model_failed', {
					default: 'AI model reset failed.',
				}),
			});
		}
	};

	async function checkAIStatus(id: string) {
		const res = await postReq('device/check_ai', {
			id: id,
		});
		if (res?.isok) {
			const {
				reset_ai_ts = 0,
				ai_model_readiness = 0,
				ai_start_learning_ts = 0,
				ai_remaining_hours = 0,
			}: AiModelData = {
				reset_ai_ts: 0,
				ai_model_readiness: 0,
				ai_start_learning_ts: 0,
				ai_remaining_hours: 0,
				...res.data,
			};
			lastResetTs = reset_ai_ts;
			progressReadinessPercent = ai_model_readiness;
			starLearningTs = ai_start_learning_ts;
			remainingHours = ai_remaining_hours;
			loading = false;
		}
	}

	const handleModalAction = async () => {
		showLoadingScreen = true;
		if (modalType === 'reset') {
			await resetAITraining();
		} else if (modalType === 'cancel' || modalType === 'erase') {
			await eraseOrCancelAIModel(device);
		} else {
			console.error('Unknown modal type');
		}
		showLoadingScreen = false;
	};

	onMount(() => {
		if (!isPremiumUser) return;
		loading = true;
		checkAIStatus(device.id);
	});

	$: numberOfAIModels = Object.values($devices).filter(
		(device) =>
			typeof device.cloud_options?.ai_device_failure !== 'undefined' &&
			devices_config[device.type]?.isPM &&
			/^(power_meter|relay|emeter)$/.test(device.category),
	).length;

	$: showAlertIfDevicesWithAIModelReachedLimit =
		typeof device?.cloud_options?.ai_device_failure === 'undefined' &&
		numberOfAIModels >= MAX_AI_MODEL_LIMIT;
</script>

{#if loading}
	<div class="d-flex justify-content-center">
		<Loader class="mt-m" size="m" />
	</div>
{:else if showLoadingScreen}
	<!-- create progress bar with Progress component who will last 1 minute -->
	<!-- Text your AI model is now resetting, please wait -->
	<Container class="d-flex flex-column align-items-center">
		<div class="d-flex justify-content-center">
			<Loader class="mt-m" size="m" />
		</div>
		<Text element="p" styleType="body-small" class="text-bold text-center  mb-l">
			{#if modalType.length !== 0}
				{$format('text_erasing_ai_model', {
					default: `Your AI model is going to be ${modalType}. This will take about ${timeToReset} minutes, please wait.`,
					values: {
						modalType: $format(`title_${modalType}_ai_model_short`),
					},
				})}
			{:else}
				{`Your AI model is updating. This will take about ${timeToReset} minutes, please wait.`}
			{/if}
		</Text>
	</Container>
{:else if !isPremiumUser}
	<AINonPremiumUserAlert {device} bind:showAboutAIModelModal />
{:else if isPremiumUser}
	{#if showAlertIfDevicesWithAIModelReachedLimit}
		<Alert
			type="warning"
			heading={$format('title_max_ai_models_ai', {
				default: `You have reached the maximum number of AI models (${MAX_AI_MODEL_LIMIT}).`,
				values: { max_ai_model_limit: MAX_AI_MODEL_LIMIT },
			})}
			isHeadingBold
		/>
	{:else if showAIIntroductionScreen}
		<AIIntroductionScreen
			{requiredHours}
			{useNewLayout}
			bind:showAboutAIModelModal
			bind:showAIIntroductionScreen
			bind:startTraining
		/>
	{:else if showAIMonitoringScreen}
		<AIMonitoringScreen
			{timeToReset}
			{showWarningIncorrectData}
			{device}
			{useNewLayout}
			bind:showAboutAIModelModal
			on:resetCancelEraseAIModelData={(e) => {
				showResetCancelEraseAIModal = true;
				modalType = e.detail;
			}}
		/>
	{:else}
		<AiModelTrainingScreen
			{timeToReset}
			{showWarningIncorrectData}
			{device}
			{useNewLayout}
			bind:enabled
			{deviceIsOnline}
			{lastResetTs}
			{starLearningTs}
			{remainingHours}
			{requiredHours}
			{progressReadinessPercent}
			bind:showAboutAIModelModal
			on:resetCancelEraseAIModelData={(e) => {
				modalType = e.detail;
				showResetCancelEraseAIModal = true;
			}}
			on:checkAIStatus={() => checkAIStatus(device.id)}
		/>
	{/if}
{/if}

<!-- About AI model modal -->
<AIMoreModal bind:showAboutAIModelModal {device} {isPremiumUser} {numberOfAIModels} />

<!-- Disable AI model modal -->
<!-- Lost all data and start from the beginning when button is toggle on again-->
<Modal
	backdropClose={false}
	title={`${resetCancelEraseAIModelModalsContent?.[modalType]?.title}?`}
	on:close={() => {
		showResetCancelEraseAIModal = false;
	}}
	showModal={showResetCancelEraseAIModal}
>
	<Container slot="content" padding="0">
		{#if modalType === 'erase'}
			<div class="d-flex justify-content-center">
				<Icon class="mb-xs" size="xxl" icon="text-danger fa-light fa-trash" />
			</div>
		{/if}
		<Text styleType="controls-large" class="mb-m">
			{resetCancelEraseAIModelModalsContent?.[modalType]?.text}
		</Text>
		<Alert type="warning" heading={resetCancelEraseAIModelModalsContent?.[modalType]?.alert} />
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			width="m"
			variant="secondary"
			responsiveSize={{ sm: 'm' }}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			on:click={() => {
				showResetCancelEraseAIModal = false;
			}}
		>
			{resetCancelEraseAIModelModalsContent?.[modalType]?.buttonSecondary}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			width="m"
			variant={modalType === 'erase' || modalType === 'cancel' ? 'danger' : 'primary'}
			responsiveSize={{ sm: 'm' }}
			on:click={() => {
				showResetCancelEraseAIModal = false;
				handleModalAction();
			}}
		>
			{resetCancelEraseAIModelModalsContent?.[modalType]?.buttonPrimary}
		</Button>
	</div>
</Modal>
